import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, History, BookOpen, Edit3, X, Check, Building2, Search, Filter } from "lucide-react";

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpand = (id) => setExpandedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [editForm, setEditForm] = useState({ title: "", content: "", business_id: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", content: "", business_id: "" });
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(null);
  const [filterBusiness, setFilterBusiness] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();
  // Business switcher only makes sense for admins or multi-business accounts (future pro plan)
  const showBizFilter = user?.role === "admin" || businesses.length > 1;

  const fetchData = async () => {
    try {
      const [entriesRes, bizRes] = await Promise.all([
        api.get("/knowledge-base"),
        api.get("/businesses"),
      ]);
      setEntries(entriesRes.data);
      setBusinesses(bizRes.data);
    } catch (e) {
      toast.error(t("kbPage.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!createForm.title.trim()) { toast.error("Title is required"); return; }
    try {
      const resp = await api.post("/knowledge-base", createForm);
      const created = resp?.data;
      console.log("[KB] save success:", created?.id, created?.title);
      toast.success(t("kbPage.created"));
      setShowCreate(false);
      setCreateForm({ title: "", content: "", business_id: "" });
      // Reset any active filter so the newly-created entry is always visible after save.
      setFilterBusiness("all");
      setSearchQuery("");
      // Optimistically prepend the new entry so it shows instantly without waiting for fetch.
      if (created && created.id) {
        setEntries((prev) => [created, ...prev.filter((e) => e.id !== created.id)]);
      }
      await fetchData();
    } catch (e) {
      console.error("[KB] save failed:", e?.response?.status, e?.response?.data, e?.message);
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `Failed to create: ${detail}` : t("kbPage.createFailed"));
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({ title: entry.title, content: entry.content, business_id: entry.business_id || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", content: "", business_id: "" });
  };

  const handleSave = async (id) => {
    try {
      await api.put(`/knowledge-base/${id}`, editForm);
      toast.success(t("kbPage.updated"));
      setEditingId(null);
      fetchData();
    } catch (e) {
      toast.error("Failed to update entry");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t("kbPage.deleteConfirm"))) return;
    try {
      await api.delete(`/knowledge-base/${id}`);
      toast.success(t("kbPage.deleted"));
      fetchData();
    } catch (e) {
      toast.error(t("kbPage.deleteFailed"));
    }
  };

  const loadVersions = async (entryId) => {
    try {
      const { data } = await api.get(`/knowledge-base/${entryId}/versions`);
      setVersions(data);
      setShowVersions(entryId);
    } catch (e) {
      toast.error(t("kbPage.versionsFailed"));
    }
  };

  const getBusinessName = (businessId) => {
    const biz = businesses.find(b => b.id === businessId);
    return biz ? biz.name : "Global";
  };

  // Filtered entries
  const filteredEntries = entries.filter(entry => {
    const matchesBusiness = filterBusiness === "all" || 
      (filterBusiness === "global" && !entry.business_id) ||
      entry.business_id === filterBusiness;
    const matchesSearch = !searchQuery || 
      entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesBusiness && matchesSearch;
  });

  return (
    <div data-testid="kb-page" className="p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("kbPage.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("kbPage.subtitle")}</p>
        </div>
        <Button
          data-testid="create-kb-btn"
          onClick={() => setShowCreate(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,230,118,0.3)]"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("kbPage.newEntry")}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="kb-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("kbPage.search")}
            className="pl-10 bg-secondary/50 border-transparent"
          />
        </div>
        {showBizFilter && (
        <Select value={filterBusiness} onValueChange={setFilterBusiness}>
          <SelectTrigger className="w-[200px] bg-secondary/50 border-transparent">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by business" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Businesses</SelectItem>
            <SelectItem value="global">Global (No Business)</SelectItem>
            {businesses.map(b => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">New Knowledge Base Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Title *</Label>
                <Input
                  data-testid="kb-create-title"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  placeholder={t("kbPage.entryTitle")}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              {showBizFilter && (
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Business</Label>
                <Select
                  value={createForm.business_id || 'global'}
                  onValueChange={(v) => setCreateForm({ ...createForm, business_id: v === 'global' ? '' : v })}
                >
                  <SelectTrigger className="bg-secondary/50 border-transparent">
                    <SelectValue placeholder="Global (all businesses)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (all businesses)</SelectItem>
                    {businesses.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Content</Label>
              <Textarea
                data-testid="kb-create-content"
                value={createForm.content}
                onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })}
                placeholder="Write your knowledge base content... You can include product info, FAQs, pricing, procedures, etc."
                rows={12}
                className="bg-secondary/50 border-transparent focus:border-primary resize-none font-mono text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="border-border">Cancel</Button>
              <Button
                data-testid="kb-save-new-btn"
                onClick={handleCreate}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Create Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={!!showVersions} onOpenChange={() => setShowVersions(null)}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Version History</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {versions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No version history available</p>
            ) : (
              versions.map((v) => (
                <Card key={v.version} className="bg-secondary/30 border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="border-primary text-primary">v{v.version}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.updated_at).toLocaleString()}
                      </span>
                    </div>
                    <h4 className="font-semibold text-sm text-foreground mb-1">{v.title}</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{v.content}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Entries List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <BookOpen className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <p className="text-lg font-semibold text-muted-foreground">
            {entries.length === 0 ? t("kbPage.noEntries") : t("kbPage.noMatching")}
          </p>
          <p className="text-sm text-muted-foreground/60 mt-1">
            {entries.length === 0 ? t("kbPage.createFirst") : t("kbPage.adjustFilters")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEntries.map((entry) => (
            <Card
              key={entry.id}
              data-testid={`kb-entry-${entry.id}`}
              className="bg-white border-gray-200 hover:border-primary/20 transition-colors duration-300"
            >
              <CardContent className="p-6">
                {editingId === entry.id ? (
                  <div className="space-y-4 animate-fade-in">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Input
                        data-testid="kb-edit-title"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="bg-secondary/50 border-transparent focus:border-primary text-lg font-semibold"
                        placeholder="Title"
                      />
                      {showBizFilter && (
                      <Select
                        value={editForm.business_id || 'global'}
                        onValueChange={(v) => setEditForm({ ...editForm, business_id: v === 'global' ? '' : v })}
                      >
                        <SelectTrigger className="bg-secondary/50 border-transparent">
                          <SelectValue placeholder="Global" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Global</SelectItem>
                          {businesses.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      )}
                    </div>
                    <Textarea
                      data-testid="kb-edit-content"
                      value={editForm.content}
                      onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                      rows={10}
                      className="bg-secondary/50 border-transparent focus:border-primary resize-none font-mono text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid="kb-save-edit-btn"
                        size="sm"
                        onClick={() => handleSave(entry.id)}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <Check className="w-3 h-3 mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit} className="border-border">
                        <X className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 cursor-pointer select-none" onClick={() => toggleExpand(entry.id)} data-testid={`kb-toggle-${entry.id}`}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-muted-foreground/60 text-xs">
                            {expandedIds.has(entry.id) ? "▾" : "▸"}
                          </span>
                          <h3 className="text-lg font-semibold text-foreground">{entry.title}</h3>
                          <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                            v{entry.version}
                          </Badge>
                          {entry.business_id ? (
                            <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                              <Building2 className="w-3 h-3 mr-1" />
                              {getBusinessName(entry.business_id)}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs border-muted text-muted-foreground">
                              Global
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Updated {new Date(entry.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`kb-edit-${entry.id}`}
                          onClick={() => startEdit(entry)}
                          className="p-2 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-primary transition-colors duration-200"
                          title={t("kbPage.editInline")}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`kb-versions-${entry.id}`}
                          onClick={() => loadVersions(entry.id)}
                          className="p-2 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-[#0A84FF] transition-colors duration-200"
                          title={t("kbPage.versionHistory")}
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`kb-delete-${entry.id}`}
                          onClick={() => handleDelete(entry.id)}
                          className="p-2 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-destructive transition-colors duration-200"
                          title={t("kbPage.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {expandedIds.has(entry.id) && (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                        {entry.content}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats */}
      {entries.length > 0 && (
        <div className="mt-6 text-sm text-muted-foreground text-center">
          {t("kbPage.showing", { shown: filteredEntries.length, total: entries.length })}
        </div>
      )}
    </div>
  );
}
